/* Full-screen timer (like Toggl's): a giant running clock over the whole app, with Start / Stop.
   Opened by the expand button next to the clock on the Timer card (every home style). It also asks the browser for real
   full screen; Escape (or the browser's own exit) leaves browser full screen but keeps the overlay filling the window,
   a second Escape or the ✕ closes it. The screen is kept awake while it shows (where the browser allows).
   Start / Stop / the clock press the Timer card's own buttons, so they work exactly like the card (same checks, same
   messages, same saving). Nothing is remembered across reloads. */
import type {Timer} from '../lib/model.ts';
import {hh} from '../lib/time.ts';
import {escapeHtml, syncScrollLock} from './common.ts';
import {flipOn, toggleFlip, owlHtml} from '../surprises.ts';

/** what the Timer card currently shows (the person selected on it): set by entries.ts on every render of the Timer tab */
export interface FsInfo { running: Timer | undefined; desc: string; client: string; project: string; task: string; who: string }
let provider: () => FsInfo | null = () => null;
export function setFsProvider(p: () => FsInfo | null) { provider = p; }

const EXPAND = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const SHRINK = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
/** the button on the Timer card (placed next to the clock by each style) */
export const fsOpenBtn = (cls = '') => `<button type="button" class="fs-open ${cls}" id="fsOpenBtn" aria-label="Full screen timer" title="Full screen timer">${EXPAND}</button>`;

/** H:MM:SS without a leading zero on the hours: 0:32:10 */
const clockText = (sec: number) => { sec = Math.max(0, Math.floor(sec)); const h = Math.floor(sec / 3600), m = Math.floor(sec % 3600 / 60), s = sec % 60;
  return h + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0'); };

/* ---------- browser full screen (with the older webkit names; iPhone has none for pages: the overlay just fills the window) ---------- */
type FsDoc = Document & {webkitFullscreenElement?: Element | null; webkitFullscreenEnabled?: boolean; webkitExitFullscreen?: () => Promise<void> | void};
type FsEl = HTMLElement & {webkitRequestFullscreen?: () => Promise<void> | void};
const d = document as FsDoc;
const fsSupported = () => !!(d.fullscreenEnabled || d.webkitFullscreenEnabled);
const inBrowserFs = () => !!(d.fullscreenElement || d.webkitFullscreenElement);
function enterBrowserFs() {
  const el = document.documentElement as FsEl;
  try {
    const p = el.requestFullscreen ? el.requestFullscreen({navigationUI: 'hide'}) : el.webkitRequestFullscreen?.();
    if (p && typeof (p as Promise<void>).catch === 'function') (p as Promise<void>).catch(() => { /* refused: the overlay still fills the window */ });
  } catch { /* not allowed here */ }
}
function exitBrowserFs() {
  if (!inBrowserFs()) return;
  try { const p = d.exitFullscreen ? d.exitFullscreen() : d.webkitExitFullscreen?.(); if (p && typeof (p as Promise<void>).catch === 'function') (p as Promise<void>).catch(() => {}); } catch { /* ignore */ }
}

/* ---------- keep the screen awake while showing ---------- */
type Lock = {released: boolean; release: () => Promise<void>};
let lock: Lock | null = null;
async function wake() {
  const wl = (navigator as Navigator & {wakeLock?: {request: (t: 'screen') => Promise<Lock>}}).wakeLock;
  if (!root || !wl || document.visibilityState !== 'visible' || (lock && !lock.released)) return;
  try { lock = await wl.request('screen'); if (!root) { void lock.release().catch(() => {}); lock = null; } } catch { lock = null; /* not allowed or unsupported */ }
}
function sleep() { if (lock) { void lock.release().catch(() => {}); lock = null; } }

/* ---------- the overlay ---------- */
let root: HTMLElement | null = null, tick: ReturnType<typeof setInterval> | undefined, leftFsAt = 0;
const q = <T extends HTMLElement = HTMLElement>(sel: string) => root!.querySelector<T>(sel)!;
const cardBtn = (id: string) => document.getElementById(id) as HTMLElement | null;

export const fsOpen = () => !!root;
export function openFs() {
  if (!root) {
    root = document.createElement('div'); root.id = 'fsRoot';
    root.innerHTML = `<div class="fs-overlay" tabindex="-1" role="dialog" aria-modal="true" aria-label="Full screen timer">
      <div class="fs-top"><span class="fs-state"><i></i><span class="fs-state-t"></span><span class="fs-owl"></span></span>
        <div class="fs-tools"><button type="button" class="fs-tool" id="fsToggle"></button>
          <button type="button" class="fs-tool" id="fsClose" aria-label="Close full screen timer" title="Close (Esc)">✕</button></div></div>
      <div class="fs-center">
        <span class="fs-clockwrap" id="fsClockWrap"><button type="button" class="fs-clock" id="fsClock"></button></span>
        <div class="fs-dec" id="fsDec"></div>
        <div class="fs-desc" id="fsDesc"></div>
        <div class="fs-proj" id="fsProj"></div>
        <button type="button" class="fs-go" id="fsGo"></button>
      </div></div>`;
    document.body.append(root);
    q('#fsClose').onclick = closeFs;
    q('#fsToggle').onclick = () => { inBrowserFs() ? exitBrowserFs() : enterBrowserFs(); };
    q('#fsGo').onclick = () => cardBtn('timerToggleBtn')?.click();          // the card's own Start / Stop
    q('#fsClock').onclick = () => { if (provider()?.running) cardBtn('timerClock')?.click(); };   // the card's "Started at" window
    wireLongPress(q('#fsClockWrap'));
    tick = setInterval(paint, 1000);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('fullscreenchange', onFsChange); document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('visibilitychange', onVisible);
    syncScrollLock();
    void wake();
  }
  if (fsSupported() && !inBrowserFs()) enterBrowserFs();
  refreshFs();
  q('.fs-overlay').focus({preventScroll: true});   // not the Stop button: a stray Space / Enter must not stop the timer
}
export function closeFs() {
  if (!root) return;
  clearInterval(tick);
  document.removeEventListener('keydown', onKey, true);
  document.removeEventListener('fullscreenchange', onFsChange); document.removeEventListener('webkitfullscreenchange', onFsChange);
  document.removeEventListener('visibilitychange', onVisible);
  root.remove(); root = null;
  exitBrowserFs(); sleep(); syncScrollLock();
  document.getElementById('fsOpenBtn')?.focus({preventScroll: true});
}
/* Little surprises: a long press (600 ms) on the clock switches the retro flip-clock look on or off (remembered on this
   device). The tap that ends a long press does nothing else (a plain tap still opens "Started at"). */
function wireLongPress(wrap: HTMLElement) {
  let t: ReturnType<typeof setTimeout> | undefined, x0 = 0, y0 = 0, fired = false;
  const cancel = () => { clearTimeout(t); t = undefined; };
  wrap.addEventListener('pointerdown', e => {
    if (e.button !== 0) return;
    fired = false; x0 = e.clientX; y0 = e.clientY; cancel();
    t = setTimeout(() => { t = undefined; fired = true; toggleFlip(); paint(); }, 600);
  });
  wrap.addEventListener('pointermove', e => { if (t && Math.hypot(e.clientX - x0, e.clientY - y0) > 10) cancel(); });
  wrap.addEventListener('pointerup', cancel); wrap.addEventListener('pointercancel', cancel); wrap.addEventListener('pointerleave', cancel);
  wrap.addEventListener('click', e => { if (fired) { fired = false; e.stopPropagation(); e.preventDefault(); } }, true);
  wrap.addEventListener('contextmenu', e => e.preventDefault());
}
/** the clock as flip cards: one card per digit; only the digits that change are redrawn (and flip, unless reduced motion) */
function paintFlip(clk: HTMLElement, text: string) {
  const cards = clk.querySelectorAll<HTMLElement>('.fd, .fc');
  if (!clk.classList.contains('flip') || cards.length !== text.length) {
    clk.classList.add('flip');
    clk.innerHTML = [...text].map(ch => ch === ':' ? '<span class="fc">:</span>' : `<span class="fd">${ch}</span>`).join('');
    clk.setAttribute('aria-label', text);
    return;
  }
  [...text].forEach((ch, i) => { const c = cards[i]; if (c.textContent !== ch) { const n = c.cloneNode() as HTMLElement; n.textContent = ch; n.classList.add('tick'); c.replaceWith(n); } });
  clk.setAttribute('aria-label', text);
}
function onKey(e: KeyboardEvent) {
  if (e.key !== 'Escape' || !root) return;
  if (document.querySelector('#modalRoot .modal-backdrop, .combo-panel, #sxCard')) return;   // Escape belongs to the window on top
  e.preventDefault();
  if (inBrowserFs()) { exitBrowserFs(); return; }                  // first Escape: out of browser full screen, overlay stays
  if (Date.now() - leftFsAt < 400) return;                          // the same Escape the browser just used to leave full screen
  closeFs();
}
function onFsChange() { if (!inBrowserFs()) leftFsAt = Date.now(); paint(); }
function onVisible() { if (document.visibilityState === 'visible') void wake(); }

/** after every redraw of the app: show what the Timer card shows now (or close, when there is no Timer card) */
export function refreshFs() {
  if (!root) return;
  if (!provider()) { closeFs(); return; }
  paint();
}
function paint() {
  if (!root) return;
  const v = provider(); if (!v) { closeFs(); return; }
  const secs = v.running ? (Date.now() - new Date(v.running.startedAt).getTime()) / 1000 : 0;
  const ov = q('.fs-overlay'); ov.classList.toggle('on', !!v.running);
  q('.fs-state-t').textContent = (v.running ? 'Running' : 'Ready') + (v.who ? ' · ' + v.who : '');
  const ow = q('.fs-owl'), ok = v.running ? 'run' : ''; if (ow.dataset.k !== ok) { ow.dataset.k = ok; ow.innerHTML = owlHtml(v.running); }   // (the owl inside: tickOwls)
  const clk = q<HTMLButtonElement>('#fsClock');
  if (flipOn()) paintFlip(clk, clockText(secs));
  else { if (clk.classList.contains('flip')) { clk.classList.remove('flip'); clk.removeAttribute('aria-label'); } clk.textContent = clockText(secs); }
  clk.disabled = !v.running; clk.title = v.running ? 'Change the start time' : '';
  q('#fsDec').textContent = hh(Math.floor(secs)) + 'h';
  const desc = q('#fsDesc'); desc.textContent = v.desc || (v.running ? '(no description)' : 'What are you working on?'); desc.classList.toggle('empty', !v.desc);
  const parts = [v.client, v.project, v.task].filter(Boolean);
  q('#fsProj').innerHTML = parts.length ? parts.map(escapeHtml).join(' <span>·</span> ') : '<span class="fs-hint">No project chosen yet: pick one on the Timer card</span>';
  const go = q<HTMLButtonElement>('#fsGo');
  const goHtml = v.running ? '<b class="sq"></b><span>Stop</span>' : '<b class="tri"></b><span>Start</span>';
  if (go.innerHTML !== goHtml) go.innerHTML = goHtml;
  go.classList.toggle('stop', !!v.running); go.setAttribute('aria-label', v.running ? 'Stop timer' : 'Start timer');
  const tg = q<HTMLButtonElement>('#fsToggle'), on = inBrowserFs();
  tg.hidden = !fsSupported();
  if (tg.dataset.on !== String(on)) { tg.dataset.on = String(on); tg.innerHTML = on ? SHRINK : EXPAND; }
  tg.setAttribute('aria-label', on ? 'Leave browser full screen' : 'Browser full screen'); tg.title = on ? 'Leave browser full screen (Esc)' : 'Browser full screen';
}
